import { useRef, useState } from "react";

import { ReactComponent as EyeIcon } from "assets/icons/eye-icon.svg";
import { ReactComponent as EyeOffIcon } from "assets/icons/eye-off-line-icon.svg";

import InputFieldWithValidation, { IInputFieldWithValidationProps } from "components/InputFieldWithValidation";
import InputFieldLabel from "components/InputFieldComponents/InputFieldLabel";
import { PaymentSourceInputName } from "v2/dataroutes/BillingData";

import type { CardType } from "components/CreditCardInputs/CardNumberInput";


export default function CVVInput(
  {
    name,
    validate = true,
    cardType = 'other',
    ...props
  }: {
    name?: string,
    validate?: boolean,
    cardType?: CardType,
  } & IInputFieldWithValidationProps
) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const [ showCVV, setShowCVV ] = useState(false);

  const isAmex = cardType === 'amex';
  const maxLen = isAmex ? 4 : 3;

  const unformatCVV = (cvvStr: string, cursorPosition: number | null): [ string, number | null ] => {
    const nonNullCursorPosition = cursorPosition || 0;
    const cvvBeforeCursor = cvvStr.slice(0, nonNullCursorPosition);
    const numbersBeforeCursor = cvvBeforeCursor.replace(/\D/g, "");
    const realCursorPosition = nonNullCursorPosition - (cvvBeforeCursor.length - numbersBeforeCursor.length);

    return [ (numbersBeforeCursor + cvvStr.slice(nonNullCursorPosition).replace(/\D/g, "")).slice(0, maxLen), realCursorPosition ];
  };

  const formatCVV = (cvv: string | ReadonlyArray<string> | number | undefined, cursorPosition?: number | null): [ string, number | null ] => {
    if (typeof cvv !== "string") {
      return [ "", 0 ];
    } else {
      return [ cvv.slice(0, maxLen), cursorPosition || null ];
    }
  };

  return (
    <>
      <input type="hidden" name={ name ?? PaymentSourceInputName.card.cvv } ref={ hiddenInputRef } />
      <InputFieldWithValidation
        { ...props }
        label={
          <InputFieldLabel
            label="Security Code"
            required={ validate }
            tooltip={ isAmex ? "The 4-digit code on the front of your Amex card." : "The 3-digit code on the back of your card." }
          />
        }
        type={ showCVV ? "text" : "password" }
        placeholder="CVV"
        pattern={ validate ? `\\d{${maxLen}}` : undefined }
        patternMismatchErrorMessage="Invalid CVV code"
        value=""
        formatValue={ formatCVV }
        unformatValue={ unformatCVV }
        unformattedInputRef={ hiddenInputRef }
        required={ validate }
        TrailingElement={
          showCVV ? (
            <EyeIcon
              className="w-5 h-5 cursor-pointer"
              onClick={ () => setShowCVV(false) }
            />
          ) : (
            <EyeOffIcon
              className="w-5 h-5 cursor-pointer"
              onClick={ () => setShowCVV(true) }
            />
          )
        }
      />
    </>
  );
};
